import { useCallback, useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import {
  sellerProductInventoriesForBuyerQuery,
  sortedLengthIdsCacheKey,
  type SellerProductInventoriesForBuyerFilters,
} from '~/api/inventory';
import {
  graphqlQueryRetry,
  runGraphqlQuery,
  STALE_TIME_MS,
} from '~/hooks/useGraphqlQuery';
import type { SellerProductInventoriesForBuyerQuery } from '~/graphql/graphql';
import { DRILL_DOWN_PAGE_SIZE } from './vendorGroupFacilitiesConstants';

type FacilityPageQueryResult =
  UseQueryResult<SellerProductInventoriesForBuyerQuery>;

export interface VendorFacilitiesResult {
  content: SellerProductInventoriesForBuyerQuery['sellerProductInventoriesForBuyer']['content'];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: Error | null;
  refetch: () => void;
}

const combineFacilityPageResults = (results: FacilityPageQueryResult[]) =>
  results;

export function useVendorGroupFacilities(
  vendorOrganizationIds: string[],
  expandedVendorIds: ReadonlySet<string>,
  baseFilter: SellerProductInventoriesForBuyerFilters,
  pageSize: number = DRILL_DOWN_PAGE_SIZE
) {
  // Collapsing and re-expanding a vendor deliberately leaves this map alone (scroll depth
  // persists), since react-query's cache makes re-fetching those pages cheap.
  const [pagesRequestedByOrgId, setPagesRequestedByOrgId] = useState<
    Record<string, number>
  >({});

  const expandedOrgIds = useMemo(
    () => vendorOrganizationIds.filter((id) => expandedVendorIds.has(id)),
    [vendorOrganizationIds, expandedVendorIds]
  );

  const normalizedDestinationLocationId =
    baseFilter.destinationLocationId?.trim() || undefined;

  // Any filter that changes which inventory is returned makes pages fetched under the old filter
  // stale. Resetting during render, rather than in an effect, avoids an extra render where
  // every previously scrolled-to page gets re-requested under the new filter.
  const lengthIdsKey = sortedLengthIdsCacheKey(
    baseFilter.platformProductLengthIds
  );

  // Identity comes from the sorted-id string, not the array: a caller passing an inline
  // `platformProductLengthIds={[id]}` would otherwise rebuild every query key and the whole
  // queries array on each render.
  const lengthIds = useMemo(
    () => baseFilter.platformProductLengthIds ?? undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- lengthIdsKey is the array's identity
    [lengthIdsKey]
  );

  const filterIdentityKey = `${lengthIdsKey}::${baseFilter.platformProductId ?? 'none'}::${normalizedDestinationLocationId ?? 'none'}::${baseFilter.deliveryMode ?? 'none'}::${baseFilter.includeInventoryLengths ?? false}`;
  const [appliedFilterIdentityKey, setAppliedFilterIdentityKey] =
    useState(filterIdentityKey);
  if (filterIdentityKey !== appliedFilterIdentityKey) {
    setAppliedFilterIdentityKey(filterIdentityKey);
    setPagesRequestedByOrgId({});
  }

  const buildFacilityQueryKey = useCallback(
    (organizationId: string, page: number) => [
      'sellerProductInventories',
      page,
      pageSize,
      lengthIdsKey,
      baseFilter.platformProductId ?? 'none',
      organizationId,
      normalizedDestinationLocationId ?? 'none',
      baseFilter.deliveryMode ?? 'none',
      baseFilter.includeInventoryLengths ?? false,
    ],
    [
      pageSize,
      lengthIdsKey,
      baseFilter.platformProductId,
      baseFilter.deliveryMode,
      baseFilter.includeInventoryLengths,
      normalizedDestinationLocationId,
    ]
  );

  // One (organizationId, page) pair per page already requested for an expanded vendor; grows via
  // loadMoreFacilities without violating rules of hooks, since useQueries is one hook call.
  const orgPagePairs = useMemo(() => {
    const pairs: { organizationId: string; page: number }[] = [];
    expandedOrgIds.forEach((organizationId) => {
      const pagesRequested = pagesRequestedByOrgId[organizationId] ?? 1;
      for (let page = 0; page < pagesRequested; page += 1) {
        pairs.push({ organizationId, page });
      }
    });
    return pairs;
  }, [expandedOrgIds, pagesRequestedByOrgId]);

  const queries = useMemo(
    () =>
      orgPagePairs.map(({ organizationId, page }) => ({
        queryKey: buildFacilityQueryKey(organizationId, page),
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          runGraphqlQuery(
            sellerProductInventoriesForBuyerQuery,
            {
              page,
              size: pageSize,
              platformProductLengthIds: lengthIds,
              platformProductId: baseFilter.platformProductId ?? undefined,
              sellerOrganizationId: organizationId,
              destinationLocationId: normalizedDestinationLocationId,
              deliveryMode: baseFilter.deliveryMode ?? undefined,
              includeInventoryLengths:
                baseFilter.includeInventoryLengths ?? undefined,
            },
            signal
          ),
        retry: graphqlQueryRetry,
        staleTime: STALE_TIME_MS,
        refetchOnWindowFocus: false,
      })),
    [
      orgPagePairs,
      buildFacilityQueryKey,
      pageSize,
      lengthIds,
      baseFilter.platformProductId,
      baseFilter.deliveryMode,
      baseFilter.includeInventoryLengths,
      normalizedDestinationLocationId,
    ]
  );

  const results = useQueries({ queries, combine: combineFacilityPageResults });

  const facilitiesByOrgId = useMemo(() => {
    const map = new Map<string, VendorFacilitiesResult>();
    expandedOrgIds.forEach((organizationId) => {
      const pageResults: FacilityPageQueryResult[] = [];
      orgPagePairs.forEach((pair, index) => {
        if (pair.organizationId === organizationId) {
          pageResults[pair.page] = results[index];
        }
      });

      const [firstPageResult, ...additionalPageResults] = pageResults;

      const content = pageResults.flatMap(
        (pageResult) =>
          pageResult?.data?.sellerProductInventoriesForBuyer?.content ?? []
      );
      const totalElements = pageResults
        .map(
          (pageResult) =>
            pageResult?.data?.sellerProductInventoriesForBuyer?.paging
              ?.totalElements
        )
        .find((value): value is number => value !== undefined);

      // Forces hasMore false and error non-null on a failed later page, instead of leaving the
      // scroll trigger to retry forever while the loading-more row silently disappears.
      const laterPageError = additionalPageResults.find(
        (pageResult) => pageResult?.error
      )?.error;

      map.set(organizationId, {
        content,
        isLoading: firstPageResult?.isLoading ?? true,
        isLoadingMore: additionalPageResults.some(
          (pageResult) => pageResult?.isLoading
        ),
        hasMore:
          !laterPageError &&
          totalElements !== undefined &&
          content.length < totalElements,
        error: firstPageResult?.error ?? laterPageError ?? null,
        refetch: () => {
          pageResults.forEach((pageResult) => void pageResult?.refetch());
        },
      });
    });
    return map;
  }, [expandedOrgIds, orgPagePairs, results]);

  const loadMoreFacilities = useCallback(
    (organizationId: string) => {
      const vendorFacilities = facilitiesByOrgId.get(organizationId);
      if (!vendorFacilities?.hasMore || vendorFacilities.isLoadingMore) return;
      setPagesRequestedByOrgId((previous) => ({
        ...previous,
        [organizationId]: (previous[organizationId] ?? 1) + 1,
      }));
    },
    [facilitiesByOrgId]
  );

  return {
    facilitiesByOrgId,
    loadMoreFacilities,
  };
}
